// `SessionRow` — single row in the active-sessions list (US-011).
//
// Renders the user-agent + IP + relative-age, plus a "Révoquer" CTA.
// The current session is marked with a "Session actuelle" pill and its
// revoke button is visually disabled (the user can sign out via the
// shell's logout, not from here).

import type { AuthSession } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Button, Pill } from '@barel/ui';
import { LogOut } from 'lucide-react';

import { formatDate } from '@/lib/format';

export interface SessionRowProps {
  session: AuthSession;
  /** Whether this row represents the user's *current* session. */
  current: boolean;
  /** Async revoker bound to `useRevokeSession().mutateAsync({ token })`. */
  onRevoke: (token: string) => Promise<void> | void;
  /** True while a revoke mutation is in-flight for this row. */
  busy?: boolean;
}

function formatUserAgent(ua: string | null | undefined, fallback: string): string {
  if (!ua) return fallback;
  // Strip versions to keep the row scannable: "Chrome on macOS".
  const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)/u);
  const osMatch = ua.match(/Mac OS X|Windows|Linux|Android|iPhone OS|iPad/u);
  if (browserMatch && osMatch) {
    return `${browserMatch[0]} · ${osMatch[0]}`;
  }
  return ua.length > 64 ? `${ua.slice(0, 64)}…` : ua;
}

export function SessionRow({ session, current, onRevoke, busy = false }: SessionRowProps) {
  const t = useT('settings');
  const tCommon = useT('common');
  const label = formatUserAgent(session.userAgent ?? null, t('security.sessionUnknownAgent'));

  return (
    <li
      data-component="SessionRow"
      data-current={current || undefined}
      className="flex items-center justify-between gap-4 border-b border-subtle px-4 py-3 last:border-b-0"
    >
      <div className="flex flex-1 flex-col gap-0.5 text-sm">
        <span className="font-medium text-primary">{label}</span>
        <span className="text-xs text-tertiary">
          {session.ipAddress ?? '—'}
          {session.expiresAt
            ? ` · ${tCommon('actions.next')}: ${formatDate(session.expiresAt)}`
            : null}
        </span>
      </div>
      {current ? (
        <Pill tone="accent" size="sm">
          {t('security.sessionCurrent')}
        </Pill>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          iconLeft={<LogOut size={14} />}
          loading={busy}
          onClick={() => {
            // Better Auth revokes by session *token*, not id.
            void onRevoke(session.token);
          }}
          data-testid={`settings-session-revoke-${session.id}`}
        >
          {t('security.sessionRevoke')}
        </Button>
      )}
    </li>
  );
}
